import axios from 'axios';

// Detect API URL using same pattern as other services
const getApiUrl = () => {
  console.log('🔍 Detecting API URL for groups...');
  console.log('🔍 Current hostname:', window.location.hostname);
  console.log('🔍 Environment API URL:', process.env.REACT_APP_API_URL);
  
  // If env variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    console.log('✅ Using env variable:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // If accessing from localhost, use localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('✅ Using localhost for development');
    return 'http://localhost:8000';
  }
  
  // Production: Use same protocol and host as frontend
  const isHttps = window.location.protocol === 'https:';
  const productionUrl = `${isHttps ? 'https' : 'http'}://${window.location.hostname}`;
  console.log('✅ Using production URL:', productionUrl);
  return productionUrl;
};

const API_BASE_URL = getApiUrl() + '/api';
console.log('🔗 Group API URL:', API_BASE_URL);

class GroupService {
  // Validate authentication before API calls
  validateAuth() {
    const token = localStorage.getItem('userToken');
    const userId = localStorage.getItem('userId');
    const userData = localStorage.getItem('userData');
    
    console.log('🔍 GroupService: Validating auth data:', {
      hasToken: !!token,
      hasUserId: !!userId,
      hasUserData: !!userData,
      userIdValue: userId,
      tokenPreview: token ? token.substring(0, 20) + '...' : null
    });
    
    if (!token || !userId || !userData) {
      console.error('❌ GroupService: Missing auth data - clearing localStorage and requiring re-login');
      localStorage.removeItem('userToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userData');
      throw new Error('Authentication required. Please log in again.');
    }
    
    // Parse and validate userData
    try {
      const parsedUserData = JSON.parse(userData);
      console.log('👤 GroupService: Parsed user data:', {
        userId: parsedUserData._id || parsedUserData.id,
        email: parsedUserData.email,
        name: parsedUserData.name
      });
      
      // Ensure userId matches the one in userData
      const actualUserId = parsedUserData._id || parsedUserData.id;
      if (actualUserId !== userId) {
        console.error('❌ GroupService: UserId mismatch!', { 
          storageUserId: userId, 
          userDataUserId: actualUserId 
        });
        // Use the userId from userData as it's more reliable
        localStorage.setItem('userId', actualUserId);
        return { token, userId: actualUserId };
      }
    } catch (error) {
      console.error('❌ GroupService: Invalid userData in localStorage', error);
      localStorage.removeItem('userToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userData');
      throw new Error('Invalid user data. Please log in again.');
    }
    
    return { token, userId };
  }

  // Get group rankings
  async getGroupRankings(groupId) {
    try {
      console.log('📊 GroupService: Getting rankings for group:', groupId);
      const { token } = this.validateAuth();

      const response = await axios.get(`${API_BASE_URL}/groups/${groupId}/rankings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('📊 Group rankings response:', response.data);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        throw new Error(response.data?.message || 'Failed to get group rankings');
      }
    } catch (error) {
      console.error('❌ GroupService: Error getting group rankings:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get group rankings';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Create group with shareable link
  async createGroup(groupData) {
    try {
      console.log('👥 GroupService: Creating group:', groupData);
      console.log('👥 API URL being used:', `${API_BASE_URL}/groups/create-with-link`);
      const { token } = this.validateAuth();

      const response = await axios.post(`${API_BASE_URL}/groups/create-with-link`, groupData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('👥 Create group response:', response.data);
      console.log('👥 Group code received:', response.data.data?.groupCode);
      
      if (response.data && response.data.success) {
        // Ensure groupCode is at the top level for easy access
        const groupData = {
          ...response.data.data,
          groupCode: response.data.data.groupCode || response.data.data.group?.groupCode
        };
        
        return {
          success: true,
          data: groupData,
          groupCode: groupData.groupCode,
          shareableLink: response.data.data.shareableLink
        };
      } else {
        throw new Error(response.data?.message || 'Failed to create group');
      }
    } catch (error) {
      console.error('❌ GroupService: Error creating group:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: `${API_BASE_URL}/groups/create-with-link`
      });
      
      if (error.response?.status === 404) {
        throw new Error('Group API endpoints not available. Please ensure the backend server is running with group routes implemented.');
      }
      
      if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message || error.response?.data?.error;
        if (backendMessage === 'Creator user not found') {
          console.error('❌ GroupService: User not found in backend database');
          console.error('❌ This suggests auth token is valid but user was deleted from backend');
          localStorage.removeItem('userToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('userId');
          throw new Error('Your account was not found. Please log in again to refresh your session.');
        }
        throw new Error(backendMessage || 'Invalid request data. Please check your input and try again.');
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create group';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Join group by code
  async joinGroup(groupCode) {
    try {
      console.log('🚪 GroupService: Joining group with code:', groupCode);
      console.log('🚪 API URL being used:', `${API_BASE_URL}/groups/join/${groupCode}`);
      const { token } = this.validateAuth();

      const response = await axios.post(`${API_BASE_URL}/groups/join/${groupCode}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('🚪 Join group response:', response.data);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      } else {
        throw new Error(response.data?.message || 'Failed to join group');
      }
    } catch (error) {
      console.error('❌ GroupService: Error joining group:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: `${API_BASE_URL}/groups/join/${groupCode}`
      });
      
      if (error.response?.status === 404) {
        throw new Error('Group not found or group API endpoints not available. Please check the group code or ensure the backend server is running.');
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to join group';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Get user's groups
  async getUserGroups() {
    try {
      console.log('📝 GroupService: Getting user groups');
      const { token, userId } = this.validateAuth();
      
      // Use the endpoint from the API docs: GET /api/groups/user/:userId/groups
      const url = `${API_BASE_URL}/groups/user/${userId}/groups`;
      console.log('📝 API URL being used:', url);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('📝 User groups response:', response.data);
      
      if (response.data && (response.data.success || Array.isArray(response.data))) {
        return {
          success: true,
          data: response.data.data || response.data || []
        };
      } else {
        throw new Error(response.data?.message || 'Failed to get user groups');
      }
    } catch (error) {
      console.error('❌ GroupService: Error getting user groups:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 404) {
        // If endpoint doesn't exist, return empty array for now
        console.log('📝 Groups endpoint not available, returning empty array');
        return {
          success: true,
          data: []
        };
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get user groups';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Remove user from group (for group creators)
  async removeUserFromGroup(groupId, userId) {
    try {
      console.log('🗑️ GroupService: Removing user from group:', { groupId, userId });
      const { token } = this.validateAuth();

      const response = await axios.delete(`${API_BASE_URL}/groups/${groupId}/members/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('🗑️ Remove user response:', response.data);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'User removed successfully'
        };
      } else {
        throw new Error(response.data?.message || 'Failed to remove user');
      }
    } catch (error) {
      console.error('❌ GroupService: Error removing user:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        url: `${API_BASE_URL}/groups/${groupId}/members/${userId}`
      });
      
      if (error.response?.status === 404) {
        throw new Error('Group or user not found.');
      }
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to remove users from this group.');
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove user';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Get public/discoverable groups
  async getPublicGroups() {
    try {
      console.log('📝 GroupService: Getting public groups');
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/groups/public`;
      console.log('📝 API URL being used:', url);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('📝 Public groups response:', response.data);
      
      if (response.data && (response.data.success || Array.isArray(response.data))) {
        return {
          success: true,
          data: response.data.data || response.data || []
        };
      } else {
        throw new Error(response.data?.message || 'Failed to get public groups');
      }
    } catch (error) {
      console.error('❌ GroupService: Error getting public groups:', error);
      
      if (error.response?.status === 404) {
        // If endpoint doesn't exist, return empty array for now
        console.log('📝 Public groups endpoint not available, returning empty array');
        return {
          success: true,
          data: []
        };
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get public groups';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Search groups by name or description
  async searchGroups(query) {
    try {
      console.log('🔍 GroupService: Searching groups:', query);
      const { token } = this.validateAuth();
      
      const url = `${API_BASE_URL}/groups/search?q=${encodeURIComponent(query)}`;
      console.log('🔍 API URL being used:', url);

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      console.log('🔍 Search groups response:', response.data);
      
      if (response.data && (response.data.success || Array.isArray(response.data))) {
        return {
          success: true,
          data: response.data.data || response.data || []
        };
      } else {
        throw new Error(response.data?.message || 'Failed to search groups');
      }
    } catch (error) {
      console.error('❌ GroupService: Error searching groups:', error);
      
      if (error.response?.status === 404) {
        // If endpoint doesn't exist, return empty array for now
        console.log('🔍 Search groups endpoint not available, returning empty array');
        return {
          success: true,
          data: []
        };
      }
      
      if (error.response?.status === 401) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to search groups';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Share group (generate shareable content)
  shareGroup(groupCode, groupName) {
    const shareableLink = `${window.location.origin}/join-group/${groupCode}`;
    const shareText = `Join "${groupName}" investment group! Use code: ${groupCode} or visit: ${shareableLink}`;
    
    return {
      link: shareableLink,
      text: shareText,
      code: groupCode
    };
  }
}

export default new GroupService();